// « When did this activity REALLY run? » — correcting a past day's activity
// window from the pointages (operator, 2026-07-29).
//
// The planned windows are a rough estimate; the punches are fact. The operator's
// rule, verbatim: « if a qualified person is there the activity is covered, or
// else it was stopped ». So an activity's real window on a past day is the span
// over which somebody who could run it was actually clocked in.
//
// This deliberately says NOTHING about who did what. Attribution stays as it is
// (the ⚡ marker is good enough, per the operator); only the START and END are
// corrected. That is why the result belongs in `board_activity_overrides` — a
// per-day fact about the ACTIVITY — and not in presence rows, which would be a
// claim about a person.

import { timeToMinutes } from './boardCoverage.js';
import { businessDayOf, parisHM } from './domain.js';
import { minutesToTime } from './staffingModel.js';

/** A clocked interval in Europe/Paris 'HH:MM'. */
export interface Interval {
  start: string;
  end: string;
}

/**
 * Merge overlapping/touching intervals into disjoint spans, in order.
 *
 * Touching counts as continuous: one person clocking out at 12:47 as another
 * clocks in at 12:47 is uninterrupted coverage, not two shifts with a
 * zero-length hole between them.
 */
export function mergeIntervals(intervals: Interval[]): Interval[] {
  const sorted = intervals
    .map((i) => ({ s: timeToMinutes(i.start), e: timeToMinutes(i.end) }))
    .filter((i) => i.e > i.s)
    .sort((a, b) => a.s - b.s);

  const merged: { s: number; e: number }[] = [];
  for (const cur of sorted) {
    const last = merged[merged.length - 1];
    if (last && cur.s <= last.e) last.e = Math.max(last.e, cur.e);
    else merged.push({ ...cur });
  }
  return merged.map((m) => ({ start: minutesToTime(m.s), end: minutesToTime(m.e) }));
}

export interface ActualWindow {
  start: string;
  end: string;
  /** Uncovered stretches INSIDE the corrected window — nobody was there. */
  gaps: Interval[];
  /** Minutes the corrected window differs from the planned one, signed. */
  startShift: number;
  endShift: number;
}

/**
 * The activity's real window on a past day, or null when the punches say
 * nothing about it.
 *
 * Takes the connected run of coverage that OVERLAPS the planned window, and
 * takes its outer bounds. The overlap test is what stops a window ballooning to
 * the whole day: someone qualified for Petit-déjeuner who also works the evening
 * bar has an evening punch that must not extend breakfast to 23:48. Coverage
 * that never touches the planned window is a different activity's, not this
 * one's, however qualified the person.
 *
 * Returns NULL when nothing overlaps — the planned window then stands. Silence
 * is not absence: an activity with no punch coverage is far more likely to be
 * one whose people cannot clock (Dima, mornings) than one that did not happen,
 * and shrinking it to nothing on that basis would rewrite the day as a lie.
 */
export function actualWindowFor(planned: Interval, intervals: Interval[]): ActualWindow | null {
  const plannedStart = timeToMinutes(planned.start);
  const plannedEnd = timeToMinutes(planned.end);
  const merged = mergeIntervals(intervals);

  const touching = merged.filter(
    (m) => timeToMinutes(m.start) < plannedEnd && plannedStart < timeToMinutes(m.end),
  );
  if (touching.length === 0) return null;

  const first = touching[0] as Interval;
  const last = touching[touching.length - 1] as Interval;

  // Holes BETWEEN the touching runs: the activity was genuinely unstaffed then
  // (a lone cleaner's lunch break). `board_activity_overrides` has one start and
  // one end and cannot express a hole, so these are REPORTED, not stored — the
  // operator sees the interruption and decides whether it matters.
  const gaps: Interval[] = [];
  for (let i = 1; i < touching.length; i++) {
    const prev = touching[i - 1] as Interval;
    const cur = touching[i] as Interval;
    gaps.push({ start: prev.end, end: cur.start });
  }

  return {
    start: first.start,
    end: last.end,
    gaps,
    startShift: timeToMinutes(first.start) - plannedStart,
    endShift: timeToMinutes(last.end) - plannedEnd,
  };
}

export interface WindowCorrection {
  activityId: number;
  label: string;
  date: string;
  planned: Interval;
  actual: ActualWindow;
  /**
   * `clocked`  — every attributed person clocks; the bounds are pure evidence.
   * `assumed`  — somebody attributed never clocks, so the span comes from their
   *              colleagues. APPLICABLE, on a standing operator assumption (see
   *              `missingClockers`), not on evidence about that person.
   */
  trust: 'clocked' | 'assumed';
  /**
   * Attributed people with no punches at all in the period.
   *
   * The correction is still applied for them, on the operator's rule
   * (2026-07-29): a non-clocker is never the one who OPENS an activity — « assume
   * there is always another person with him, so the start time will be the
   * earliest start time of the other persons ». Dima works Nettoyage mornings
   * and has no clock; somebody who does is always there with him.
   *
   * Recorded rather than hidden because it is an ASSUMPTION, not a measurement.
   * The day it stops holding — he opens alone, or another non-clocker joins — the
   * corrected starts drift late and nothing else would say why.
   */
  missingClockers: string[];
}

export interface CorrectionInput {
  date: string;
  activities: { id: number; label: string; windowStart: string; windowEnd: string }[];
  qualifications: { employeeId: string; activityId: number }[];
  /** Who is ATTRIBUTED to each activity that day (declared/assigned/⚡ rows). */
  attributed: { employeeId: string; activityId: number }[];
  /**
   * « Activité principale » per person — what they NORMALLY do. Used when no
   * per-day attribution exists, which is the whole June–July situation: the
   * planning back then was a rough estimate, so the standing rule is better
   * evidence than the day's rows.
   */
  mains?: { employeeId: string; activityId: number }[];
  /** Each person's clocked intervals for the day, Europe/Paris 'HH:MM'. */
  intervalsByEmployee: Map<string, Interval[]>;
  /**
   * People who never clock at all over the period — Dima, who has no kiosk
   * access. NOT the same as « did not clock today »: someone off today still
   * clocks in general, so their silence carries information and his does not.
   */
  neverClock?: ReadonlySet<string>;
  /** Below this many minutes of change, leave the window alone. */
  minShiftMinutes?: number;
}

/**
 * Propose window corrections for one past day.
 *
 * People are taken from the ATTRIBUTION first and only fall back to « anyone
 * qualified » when nothing is attributed. Both readings satisfy the operator's
 * rule, but attribution is tighter: on the screenshot's day, Dominique's 05:49
 * punch was breakfast, and letting it start Nettoyage at 05:49 merely because
 * she is also a qualified cleaner would move a window on evidence about a
 * different activity.
 *
 * A correction smaller than `minShiftMinutes` is dropped — a window that moves
 * by four minutes is noise, and a day full of four-minute overrides buries the
 * ones that mean something.
 */
export function proposeWindowCorrections(input: CorrectionInput): WindowCorrection[] {
  const minShift = input.minShiftMinutes ?? 10;
  const neverClock = input.neverClock ?? new Set<string>();

  const group = (rows: { employeeId: string; activityId: number }[]): Map<number, string[]> => {
    const m = new Map<number, string[]>();
    for (const r of rows) m.set(r.activityId, [...(m.get(r.activityId) ?? []), r.employeeId]);
    return m;
  };
  const attributedBy = group(input.attributed);
  const mainsBy = group(input.mains ?? []);
  const qualifiedBy = group(input.qualifications);

  const out: WindowCorrection[] = [];
  for (const activity of input.activities) {
    // Precedence, narrowest evidence first: who the day says was on it, else who
    // normally does it, else anyone who could. Qualification last because it is
    // the loosest — using it first is what would start Nettoyage at 05:49 on a
    // breakfast punch.
    const people =
      attributedBy.get(activity.id) ?? mainsBy.get(activity.id) ?? qualifiedBy.get(activity.id) ?? [];
    if (people.length === 0) continue;

    const intervals = people.flatMap((id) => input.intervalsByEmployee.get(id) ?? []);
    if (intervals.length === 0) continue;

    const planned: Interval = { start: activity.windowStart, end: activity.windowEnd };
    const actual = actualWindowFor(planned, intervals);
    if (!actual) continue;
    if (Math.abs(actual.startShift) < minShift && Math.abs(actual.endShift) < minShift) continue;

    // The Dima case. His activity's bounds come from his colleagues, which the
    // operator accepts because he never opens a shift alone — so the correction
    // stands, flagged `assumed` so the reason is visible rather than folded away.
    const missingClockers = people.filter((id) => neverClock.has(id));

    out.push({
      activityId: activity.id,
      label: activity.label,
      date: input.date,
      planned,
      actual,
      trust: missingClockers.length > 0 ? 'assumed' : 'clocked',
      missingClockers,
    });
  }
  return out;
}

/**
 * Who never clocks over the whole period.
 *
 * Derived from the data rather than a flag: anybody with zero punches across
 * two months is not « off », they are outside the clock. That reads the same
 * whether the cause is no kiosk access, a paper timesheet, or Connecteam not
 * covering them — and it needs no column that could go stale.
 */
export function neverClockedIn(
  employeeIds: string[],
  intervalsByEmployeeByDate: Map<string, Map<string, Interval[]>>,
): Set<string> {
  const seen = new Set<string>();
  for (const byEmployee of intervalsByEmployeeByDate.values()) {
    for (const [employeeId, intervals] of byEmployee) {
      if (intervals.length > 0) seen.add(employeeId);
    }
  }
  return new Set(employeeIds.filter((id) => !seen.has(id)));
}

// ── The ongoing indicator: predicted window vs realised ─────────────────────
//
// « I expect the prévisionnel to fill the gap between approximation for start/end
// activities planned (because they really depend on load) — maybe the on-going
// indicator would be to show real vs actual start time of an activity »
// (operator, 2026-07-29).
//
// The planned window is an approximation, the load-derived window is a
// prediction, and the punches are the truth. Measuring the third against the
// first two is what tells the operator whether to trust the model — and it is
// the only feedback the coefficients ever get.

/**
 * Every day's comparison, INCLUDING the days that matched well.
 *
 * Separate from `proposeWindowCorrections` for a statistical reason, not a
 * stylistic one: that function drops shifts below its threshold, because a
 * four-minute override is noise. Measuring accuracy over what it returns would
 * therefore average only the days the model got WRONG, and report a large bias
 * on an activity that is usually right. Sampling bias, built into the API — so
 * the measuring path has its own entry point that never filters.
 */
export function windowSamples(input: CorrectionInput): WindowCorrection[] {
  return proposeWindowCorrections({ ...input, minShiftMinutes: 0 });
}

export interface WindowAccuracy {
  activityId: number;
  label: string;
  /** Days with a usable comparison. */
  days: number;
  /** Signed minutes, MEDIAN: negative = really started earlier than planned. */
  medianStartShift: number;
  medianEndShift: number;
  /** Spread of the start shift (median absolute deviation) — how erratic the day is. */
  startVariability: number;
  /** Days resting on the non-clocker assumption rather than on evidence. */
  assumedDays: number;
  /** Operator-facing French, one line. */
  verdict: string;
}

/**
 * Typical deviation past which a start can no longer be published as a fixed
 * time. Operational, not statistical: staff need to know when to turn up.
 */
export const LOAD_DEPENDENT_MAD_MINUTES = 30;

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? Math.round(((sorted[mid - 1] as number) + (sorted[mid] as number)) / 2)
    : (sorted[mid] as number);
}

function fmtDelta(minutes: number): string {
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  const span = h > 0 ? `${h} h ${String(m).padStart(2, '0')}` : `${m} min`;
  return `${minutes < 0 ? '−' : '+'}${span}`;
}

/**
 * Per-activity accuracy over a period.
 *
 * MEDIAN, not mean: one forgotten clock-out or one exceptional day would drag a
 * mean far enough to change the verdict, and the whole point of this number is
 * that the operator can act on it. The median absolute deviation comes with it,
 * because « always 40 min late » and « anywhere between 2 h early and 2 h late »
 * are different problems — the first is a wrong configured window, the second is
 * an activity whose start genuinely depends on the load and should be DERIVED
 * rather than configured at all.
 */
export function windowAccuracy(samples: WindowCorrection[]): WindowAccuracy[] {
  const byActivity = new Map<number, WindowCorrection[]>();
  for (const s of samples) byActivity.set(s.activityId, [...(byActivity.get(s.activityId) ?? []), s]);

  const out: WindowAccuracy[] = [];
  for (const [activityId, rows] of byActivity) {
    const starts = rows.map((r) => r.actual.startShift);
    const ends = rows.map((r) => r.actual.endShift);
    const medStart = median(starts);
    const medEnd = median(ends);
    const variability = median(starts.map((s) => Math.abs(s - medStart)));
    const assumedDays = rows.filter((r) => r.trust === 'assumed').length;

    const parts: string[] = [];
    if (Math.abs(medStart) >= 20) {
      parts.push(`commence ${medStart < 0 ? 'plus tôt' : 'plus tard'} que prévu (${fmtDelta(medStart)})`);
    }
    if (Math.abs(medEnd) >= 20) {
      parts.push(`finit ${medEnd < 0 ? 'plus tôt' : 'plus tard'} que prévu (${fmtDelta(medEnd)})`);
    }
    if (parts.length === 0) parts.push('horaire planifié fidèle au réel');
    // A wide spread with a small bias means the window is not WRONG, it is
    // load-dependent — an argument for deriving it rather than editing it.
    //
    // The 30-minute threshold is operational, not statistical: past about half an
    // hour of typical variation you can no longer put the start on a printed
    // planning, because staff need to know when to turn up. Below it, a fixed
    // window is still usable.
    if (variability >= LOAD_DEPENDENT_MAD_MINUTES) {
      parts.push('très variable — dépend de la charge, à faire calculer plutôt qu’à fixer');
    }

    out.push({
      activityId,
      label: rows[0]?.label ?? String(activityId),
      days: rows.length,
      medianStartShift: medStart,
      medianEndShift: medEnd,
      startVariability: variability,
      assumedDays,
      verdict: parts.join(' · '),
    });
  }
  return out.sort((a, b) => Math.abs(b.medianStartShift) - Math.abs(a.medianStartShift));
}

/**
 * Raw clock entries → per-business-day, per-employee intervals.
 *
 * BUSINESS day (Paris, 04:00 cutoff), so a service ending at 01:00 stays on the
 * day it started rather than landing in the next morning's comparison.
 *
 * An entry with no clock-out becomes a ZERO-LENGTH interval, matching the corpus
 * convention: on a closed day it means somebody forgot to badge out, and
 * stretching it would invent hours nobody worked. `mergeIntervals` drops
 * zero-length spans, so such a punch contributes nothing to a window rather than
 * corrupting it.
 */
export function intervalsByDay(
  entries: { employeeId: string; clockIn: string; clockOut: string | null }[],
): Map<string, Map<string, Interval[]>> {
  const out = new Map<string, Map<string, Interval[]>>();
  for (const e of entries) {
    const day = businessDayOf(new Date(e.clockIn));
    const byEmployee = out.get(day) ?? new Map<string, Interval[]>();
    const start = parisHM(e.clockIn);
    const interval: Interval = { start, end: e.clockOut ? parisHM(e.clockOut) : start };
    byEmployee.set(e.employeeId, [...(byEmployee.get(e.employeeId) ?? []), interval]);
    out.set(day, byEmployee);
  }
  return out;
}
